import { Button } from '@gurupay/gp-ui-library';
import { triggerGAEvent } from '@utils/useGAEvent.ts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SigningTarget } from '../../api/hooks/useSigningPayments.ts';
import {
	useInitiatePaymentSigning,
	useSignPayment,
} from '../../api/hooks/useSigningPayments.ts';
import Alert from '../alert/index.ts';
import { TfaSigning } from './TfaSigning.tsx';

enum SigningStatus {
	SUCCESS = 'success',
	ERROR = 'error',
	CANCELED = 'canceled',
}

interface PaymentTfaProps {
	transactionId?: string;
	onSign?: (status: SigningStatus) => void;
	showSignLaterButton?: boolean;
	type?: 'transaction' | 'batch' | 'multiple';
	transactionIds?: string[];
	setSigningInitiated?: (status: boolean) => void;
}

type SigningResult = 'error' | 'success';

const PaymentTfa = ({
	onSign,
	setSigningInitiated,
	showSignLaterButton = true,
	transactionId,
	transactionIds,
	type = 'transaction',
}: PaymentTfaProps) => {
	const { t } = useTranslation('', { keyPrefix: 'tfa' });
	const initiateSigning = useInitiatePaymentSigning();
	const signPayment = useSignPayment();
	const [isVisible, setVisible] = useState(true);
	const [signingResult, setSigningResult] = useState<SigningResult>();
	const [errorSignal, setErrorSignal] = useState(0);

	const getSigningTarget = (): SigningTarget | undefined => {
		if (type === 'transaction' && transactionId) return { type, transactionId };
		if (type === 'batch' && transactionId) return { type, batchId: transactionId };
		if (type === 'multiple' && transactionIds) return { type, transactionIds };

		return undefined;
	};

	const withSigningTarget = (
		request: (target: SigningTarget) => Promise<unknown>,
	): Promise<unknown> => {
		const target = getSigningTarget();

		return target
			? request(target)
			: Promise.reject(new Error('Missing signing transaction identifier'));
	};

	const requestOtp = () => {
		setSigningResult(undefined);
		setSigningInitiated?.(true);

		return withSigningTarget((target) => initiateSigning.mutateAsync(target)).catch(
			(error: unknown) => {
				setSigningResult('error');
				setSigningInitiated?.(false);
				throw error;
			},
		);
	};

	const handleSignSuccess = () => {
		if (type === 'transaction') {
			triggerGAEvent('transaction_success');
		}
		onSign?.(SigningStatus.SUCCESS);
		setSigningInitiated?.(false);
		setSigningResult('success');
	};

	const handleSignError = () => {
		onSign?.(SigningStatus.ERROR);
		setSigningInitiated?.(false);
		setSigningResult('error');
		setErrorSignal((currentSignal) => currentSignal + 1);
	};

	const handleConfirm = (code: string) => {
		withSigningTarget((target) => signPayment.mutateAsync({ code, target }))
			.then(handleSignSuccess)
			.catch(handleSignError);
	};

	const handleSignLater = () => {
		setSigningInitiated?.(false);
		setVisible(false);
		onSign?.(SigningStatus.CANCELED);
	};

	if (!isVisible) return null;

	if (signingResult === 'success') {
		return <Alert type="success">{t('alerts.signed')}</Alert>;
	}

	const buttons = showSignLaterButton && (
		<Button className="w-full sm:w-max" onClick={handleSignLater} variant="outlined">
			{t('buttons.signLater')}
		</Button>
	);
	const notification = signingResult === 'error' && (
		<Alert type="error">{t('alerts.failed')}</Alert>
	);

	return (
		<TfaSigning
			buttons={buttons}
			errorSignal={errorSignal}
			isConfirmDisabled={signPayment.isPending}
			isRequestDisabled={initiateSigning.isPending || signPayment.isPending}
			notification={notification}
			onConfirm={handleConfirm}
			onCountdownEnd={() => setSigningInitiated?.(false)}
			requestOtp={requestOtp}
			submitButtonProps={{ children: t('buttons.sign') }}
		/>
	);
};

export { PaymentTfa, SigningStatus };
